/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  ChevronLeft,
  Calendar,
  Clock,
  Mail,
  User,
  Phone,
  Building,
  FileDown,
  FileText,
  ChevronRight,
  ClipboardCheck,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  Check,
  HelpCircle,
  Sparkles,
  Loader2,
  Type,
  Pencil,
  RotateCcw,
  X,
  Plus,
  Trash2,
  MessageSquare,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { StudentAvatar } from '../Common/StudentAvatar';
import { useInAppDialog } from '../Common/InAppDialog';

interface SignatureInputProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  defaultSignerName?: string;
}

const SignatureInput: React.FC<SignatureInputProps> = ({ label, value, onChange, disabled, placeholder, helperText, defaultSignerName }) => {
  const [mode, setMode] = useState<'type' | 'draw'>(value.startsWith('data:image') ? 'draw' : 'type');
  const sigCanvas = React.useRef<SignatureCanvas | null>(null);

  const handleClear = () => {
    sigCanvas.current?.clear();
    onChange('');
  };

  const handleDrawEnd = () => {
    if (sigCanvas.current) {
      // Use toDataURL directly to avoid crash in getTrimmedCanvas dependency
      onChange(sigCanvas.current.toDataURL('image/png'));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</label>
        {!disabled && (
          <div className="flex items-center space-x-2">
            {!value && defaultSignerName && (
              <button 
                type="button"
                onClick={() => onChange(defaultSignerName)}
                className="text-[9px] font-black uppercase text-[#0094c8] hover:underline transition-all"
              >
                Sign as {defaultSignerName}
              </button>
            )}
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              <button 
                type="button"
                onClick={() => setMode('type')}
                className={`p-1 rounded ${mode === 'type' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}
                title="Type Signature"
              >
                <Type className="w-3.5 h-3.5" />
              </button>
              <button 
                type="button"
                onClick={() => setMode('draw')}
                className={`p-1 rounded ${mode === 'draw' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}
                title="Draw Signature"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={`relative rounded-2xl border transition-all ${disabled ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-200 focus-within:ring-2 focus-within:ring-slate-900 focus-within:border-slate-900'}`}>
        {mode === 'type' ? (
          <input 
            type="text" 
            placeholder={placeholder || "Type full name to sign"}
            value={value.startsWith('data:image') ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="w-full p-4 bg-transparent text-sm font-serif italic text-blue-900 focus:outline-none disabled:opacity-50"
          />
        ) : (
          <div className={`relative flex flex-col ${disabled ? 'pointer-events-none' : ''}`}>
            {disabled && value.startsWith('data:image') ? (
              <div className="p-4 flex items-center justify-center min-h-[100px]">
                <img src={value} alt="Signature" className="max-h-[80px] object-contain" />
              </div>
            ) : (
              <>
                <div className="p-1 border-b border-dashed border-slate-100">
                  <SignatureCanvas 
                    ref={(ref) => sigCanvas.current = ref}
                    penColor="#1e3a8a"
                    canvasProps={{
                      className: 'w-full h-24 cursor-crosshair',
                      style: { display: 'block' }
                    }}
                    onEnd={handleDrawEnd}
                  />
                </div>
                {!disabled && (
                  <button 
                    onClick={handleClear}
                    className="absolute top-2 right-2 p-1.5 bg-white shadow-sm border border-slate-100 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
        <div className="absolute top-1/2 right-4 -translate-y-1/2 opacity-20 pointer-events-none">
          {value ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <AlertCircle className="w-6 h-6" />}
        </div>
      </div>
      <p className="text-[10px] text-slate-400 font-medium italic">{helperText}</p>
    </div>
  );
};

import { GRADUATE_ATTRIBUTES, VOCATIONAL_SKILLS } from '../../data/evaluationConstants';
import { PlacementEvaluation, EvaluationScore, TrainingAgreement } from '../../types';

import { TrainingAgreementDoc } from './TrainingAgreementDoc';

export const PlacementDetail: React.FC = () => {
  const {
    activeUser,
    activeTenant,
    placements,
    users,
    setCurrentView,
    updatePlacementEvaluation,
    updatePlacementAgreement,
    hireStudent,
    addPlacementNote,
    deletePlacementNote,
    viewingStudentId,
    recommendations,
    interviews,
  } = useWBL();

  const { showAlert, showConfirm, showToast } = useInAppDialog();

  const isDelegate = activeUser?.role === 'parent';

  const placementId = sessionStorage.getItem('selectedPlacementId');
  const placement = placements.find(p => p.id === placementId);
  const student = users?.find(u => u.uid === placement?.studentId);

  const [showDoc, setShowDoc] = useState(false);
  const [activeEvalTab, setActiveEvalTab] = useState<1 | 2 | null>(null);
  const [editingEval, setEditingEval] = useState<PlacementEvaluation | null>(null);
  const [evalNum, setEvalNum] = useState<1 | 2 | null>(null);
  
  const [generatingAI, setGeneratingAI] = useState<Record<string, boolean>>({});
  
  const [aiError, setAiError] = useState<Record<string, string>>({});

  const groupedSkillsByCategory = useMemo(() => {
    const groups: Record<string, typeof VOCATIONAL_SKILLS> = {};
    VOCATIONAL_SKILLS.forEach(skill => {
      const category = skill.category || 'Other Skills';
      if (!groups[category]) groups[category] = [];
      groups[category].push(skill);
    });
    return groups;
  }, []);

  const currentSkillIds = editingEval?.vocationalSkillIds || ['leadership', 'computer_tech', 'database_usage'];

  const handleSkillSelectionChange = (index: number, newSkillId: string) => {
    setEditingEval(prev => {
      if (!prev) return null;
      const oldSkillIds = prev.vocationalSkillIds || ['leadership', 'computer_tech', 'database_usage'];
      const oldSkillId = oldSkillIds[index];
      const newSkillIds = [...oldSkillIds];
      newSkillIds[index] = newSkillId;

      const newScores = { ...prev.scores };
      if (oldSkillId && oldSkillId !== newSkillId) {
        if (newScores[oldSkillId]) {
          newScores[newSkillId] = { ...newScores[oldSkillId] };
          delete newScores[oldSkillId];
        }
      }

      return {
        ...prev,
        vocationalSkillIds: newSkillIds,
        scores: newScores
      };
    });
  };

  const handleAddSkillSlot = () => {
    setEditingEval(prev => {
      if (!prev) return null;
      const oldSkillIds = prev.vocationalSkillIds || ['leadership', 'computer_tech', 'database_usage'];
      if (oldSkillIds.length >= 5) return prev;

      const nextAvailable = VOCATIONAL_SKILLS.find(s => !oldSkillIds.includes(s.id)) || VOCATIONAL_SKILLS[0];
      return {
        ...prev,
        vocationalSkillIds: [...oldSkillIds, nextAvailable.id]
      };
    });
  };

  const handleRemoveSkillSlot = (index: number) => {
    setEditingEval(prev => {
      if (!prev) return null;
      const oldSkillIds = prev.vocationalSkillIds || ['leadership', 'computer_tech', 'database_usage'];
      if (oldSkillIds.length <= 3) return prev;

      const targetSkillId = oldSkillIds[index];
      const newSkillIds = oldSkillIds.filter((_, idx) => idx !== index);

      const newScores = { ...prev.scores };
      if (targetSkillId) {
        delete newScores[targetSkillId];
      }

      return {
        ...prev,
        vocationalSkillIds: newSkillIds,
        scores: newScores
      };
    });
  };

  const [isEditingAgreement, setIsEditingAgreement] = useState(false);
  const [agreementForm, setAgreementForm] = useState<TrainingAgreement | null>(null);

  const canEditAgreement = Boolean(
    !isDelegate &&
    activeUser?.role !== 'student' &&
    activeUser?.role !== 'parent' &&
    (activeUser?.role === 'advisor' ||
     activeUser?.role === 'regional_admin' ||
     activeUser?.role === 'superadmin' ||
     activeUser?.role === 'school_admin' ||
     activeUser?.role === 'organization')
  );

  const hasUnansweredAgreementQuestions = useMemo(() => {
    if (!placement) return false;
    const agr = placement.agreement;
    if (!agr) return true;
    return (
      agr.isAlignedToIGP === undefined ||
      agr.hasInsurance === undefined ||
      agr.earnedCourseCredit === undefined ||
      agr.isPaid === undefined
    );
  }, [placement?.agreement]);

  const handleOpenEditAgreement = () => {
    if (!canEditAgreement || !placement) return;
    const agr = placement.agreement || {
      isAlignedToIGP: undefined,
      isPaid: true,
      earnedCourseCredit: undefined,
      courseCode: '',
      hasInsurance: undefined,
      jobDescription: '',
      learningObjectives: [],
      startDate: placement.startDate || '',
      endDate: placement.endDate || '',
      assignedDays: placement.assignedDays || [],
      careerPathway: '',
      worksiteName: placement.organizationName || '',
      assignedHoursTotal: placement.assignedHoursTotal ?? 120,
      supervisorName: placement.mentorName || '',
      supervisorPhone: placement.directLine || '',
      supervisorEmail: placement.registryContact || ''
    };
    setAgreementForm({
      isAlignedToIGP: agr.isAlignedToIGP !== undefined ? agr.isAlignedToIGP : undefined,
      isPaid: agr.isPaid ?? true,
      earnedCourseCredit: agr.earnedCourseCredit !== undefined ? agr.earnedCourseCredit : undefined,
      courseCode: agr.courseCode || '',
      hasInsurance: agr.hasInsurance !== undefined ? agr.hasInsurance : undefined,
      jobDescription: agr.jobDescription || '',
      learningObjectives: agr.learningObjectives || [],
      startDate: agr.startDate || placement.startDate || '',
      endDate: agr.endDate || placement.endDate || '',
      assignedDays: agr.assignedDays || placement.assignedDays || [],
      careerPathway: agr.careerPathway || '',
      worksiteName: agr.worksiteName || placement.organizationName || '',
      assignedHoursTotal: agr.assignedHoursTotal ?? placement.assignedHoursTotal ?? 120,
      supervisorName: agr.supervisorName || placement.mentorName || '',
      supervisorPhone: agr.supervisorPhone || placement.directLine || '',
      supervisorEmail: agr.supervisorEmail || placement.registryContact || ''
    });
    setIsEditingAgreement(true);
  };

  const handleSaveAgreement = async () => {
    if (!canEditAgreement || !agreementForm || !placement) return;
    try {
      await updatePlacementAgreement(placement.id, agreementForm);
      setIsEditingAgreement(false);
      setAgreementForm(null);
    } catch (err) {
      console.error("Failed to save training agreement:", err);
    }
  };

  if (!placement) {
    return (
      <div className="p-12 text-center text-slate-500">
        <p>Placement not found.</p>
        <button onClick={() => setCurrentView('placements')} className="mt-4 text-blue-600 font-bold uppercase text-[10px] tracking-widest">
          Return to List
        </button>
      </div>
    );
  }

  const handleStartEval = (num: 1 | 2) => {
    const existing = num === 1 ? placement.eval1 : placement.eval2;
    setEditingEval(existing || { status: 'in_progress', scores: {} });
    setEvalNum(num);
    setActiveEvalTab(null); // Close the record list
  };

  const handleScoreChange = (attributeId: string, score: number) => {
    setEditingEval(prev => {
      if (!prev) return null;
      return {
        ...prev,
        scores: {
          ...prev.scores,
          [attributeId]: {
            ...(prev.scores[attributeId] || { comments: '' }),
            score
          }
        }
      };
    });
  };

  const handleCommentChange = (attributeId: string, comments: string) => {
    setEditingEval(prev => {
      if (!prev) return null;
      return {
        ...prev,
        scores: {
          ...prev.scores,
          [attributeId]: {
            ...(prev.scores[attributeId] || { score: 0 }),
            comments
          }
        }
      };
    });
  };

  const handleAIGenerate = async (attrId: string, label: string, score: number, expectations?: string[]) => {
    if (!score) return;
    
    setGeneratingAI(prev => ({ ...prev, [attrId]: true }));
    setAiError(prev => ({ ...prev, [attrId]: '' }));
    try {
      const response = await fetch('/api/evaluate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentName: placement.studentName,
          attribute: label,
          score: score,
          expectations: expectations?.join(', ')
        })
      });
      
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json().catch(() => ({})) : {};
      if (response.status === 429) {
        setAiError(prev => ({ ...prev, [attrId]: 'Quota hit - wait 30s.' }));
        return;
      }
      if (data && data.comment) {
        handleCommentChange(attrId, data.comment);
      }
    } catch (err) {
      console.error("AI Generation failed:", err);
      setAiError(prev => ({ ...prev, [attrId]: 'AI currently busy.' }));
    } finally {
      setGeneratingAI(prev => ({ ...prev, [attrId]: false }));
    }
  };

  const handleSaveEval = async (status: 'in_progress' | 'released') => {
    if (!placementId || !evalNum || !editingEval) return;
    try {
      await updatePlacementEvaluation(placementId, evalNum, {
        ...editingEval,
        status,
        submittedAt: status === 'released' ? new Date().toISOString() : (editingEval.submittedAt || null)
      });
      setEvalNum(null);
      setEditingEval(null);
    } catch (err) {
      console.error("Failed to save evaluation:", err);
    }
  };

  const isOrg = activeUser?.role === 'organization' && !isDelegate;
  const canSignAsSupervisor = (isOrg && activeUser?.orgName === placement.organizationName) || activeUser?.role === 'superadmin';
  const canSignAsStudent = (activeUser?.role === 'student' && activeUser?.uid === placement.studentId && !isDelegate) || activeUser?.role === 'superadmin';
  const canSignAsAdvisor = (
    activeUser?.role === 'superadmin' || 
    activeUser?.role === 'regional_admin' || 
    activeUser?.role === 'advisor'
  ) && !isDelegate;

  return (
    <div className="w-[95%] mx-auto py-6 space-y-6">
      {/* Header & Back */}
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setCurrentView('placements')}
          className="flex items-center text-slate-500 hover:text-slate-900 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          <span className="text-[10px] font-black uppercase tracking-widest">Back to Placements</span>
        </button>
        <div className="flex items-center space-x-3">
          {canEditAgreement && (
            <button 
              onClick={handleOpenEditAgreement}
              className="flex items-center space-x-2 bg-[#0094c8] px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#007ba6] transition-all shadow-sm"
            >
              <FileText className="w-4 h-4" />
              <span>Manage Training Agreement</span>
            </button>
          )}

          <button 
            onClick={() => setShowDoc(true)}
            className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2 rounded-xl text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
          >
            <FileDown className="w-4 h-4 text-emerald-600" />
            <span>Generate Agreement PDF</span>
          </button>
          
          {isOrg && placement.status !== 'hired' && !evalNum && (
             <button 
                onClick={() => hireStudent(placement.id)}
                className="flex items-center space-x-2 bg-emerald-600 px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-sm"
             >
                <Check className="w-4 h-4" />
                <span>Mark as Hired</span>
             </button>
          )}
        </div>
      </div>

      {hasUnansweredAgreementQuestions && canEditAgreement && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-700 shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs font-black uppercase text-amber-900 tracking-wider">Training Agreement Compliance Questions Pending</h4>
              <p className="text-xs font-medium text-amber-700">Required compliance questions (IGP Alignment, Course Credit, School Insurance, etc.) are unanswered for this placement.</p>
            </div>
          </div>
          <button
            onClick={handleOpenEditAgreement}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-sm shrink-0 flex items-center space-x-1.5"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Answer Questions</span>
          </button>
        </div>
      )}

      {showDoc && (
        <TrainingAgreementDoc 
          placement={placement} 
          onClose={() => setShowDoc(false)} 
          onEditAgreement={canEditAgreement ? handleOpenEditAgreement : undefined}
        />
      )}

      {!evalNum ? (
        <>
          {/* Main Card Container */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden flex flex-col">
            {/* Top Section: Left = Placement Details & Profile, Right = Review / Evaluations & Progress */}
            <div className="flex flex-col md:flex-row border-b border-slate-100">
              {/* Left Section: Student & Worksite Details Profile */}
              <div className="md:w-1/3 lg:w-2/5 p-8 bg-slate-50/50 border-b md:border-b-0 md:border-r border-slate-100 flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md relative">
                  <StudentAvatar 
                    studentId={placement.studentId} 
                    studentName={placement.studentName} 
                    size="xl"
                  />
                </div>
                <h2 className="text-xl font-extrabold text-slate-900 mt-4 uppercase">{placement.studentName}</h2>
                <p className="text-[#0094c8] text-xs font-black uppercase tracking-widest mt-1">Active Intern</p>
                
                {/* Divider */}
                <div className="w-full border-t border-slate-200/60 my-6" />

                {/* Worksite Partner Profile */}
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md relative flex items-center justify-center bg-slate-50">
                  <StudentAvatar 
                    studentId={placement.organizationId || placement.tenantId} 
                    studentName={placement.organizationName} 
                    size="xl"
                  />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mt-4 uppercase tracking-tight">{placement.organizationName}</h3>
                <p className="text-[#0094c8] text-xs font-black uppercase tracking-widest mt-1">Worksite Partner</p>
                
                <div className="w-full mt-8 grid grid-cols-2 gap-4 text-left">
                  <div>
                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Placement Cycle</h4>
                    <p className="text-xs font-bold text-slate-800">{placement.startDate} – {placement.endDate}</p>
                  </div>
                  <div>
                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Assigned Days</h4>
                    <p className="text-xs font-bold text-slate-800">{(placement.assignedDays || []).join(', ')}</p>
                  </div>
                  <div>
                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Communication Node</h4>
                    <p className="text-xs font-bold text-slate-800">{placement.studentEmail || 'N/A'}</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Academic Institution</h4>
                      <p className="text-xs font-bold text-slate-800">{student?.school || 'N/A'}</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Career Pathway</h4>
                      <p className="text-xs font-bold text-slate-800">{placement.agreement?.careerPathway || ''}</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">IGP Aligned</h4>
                      <p className="text-xs font-bold text-slate-800">
                        {placement.agreement?.isAlignedToIGP !== undefined ? (placement.agreement.isAlignedToIGP ? 'Yes' : 'No') : ''}
                      </p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">School Insurance</h4>
                      <p className="text-xs font-bold text-slate-800">
                        {placement.agreement?.hasInsurance !== undefined ? (placement.agreement.hasInsurance ? 'Provided' : 'Not Provided') : ''}
                      </p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Course Credit</h4>
                      <p className="text-xs font-bold text-slate-800">
                        {placement.agreement?.earnedCourseCredit !== undefined ? (placement.agreement.earnedCourseCredit ? `Yes ${placement.agreement.courseCode ? `(${placement.agreement.courseCode})` : ''}` : 'No') : ''}
                      </p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Placement Type</h4>
                      <p className="text-xs font-bold text-slate-800">{placement.agreement?.isPaid ? 'Paid' : 'Unpaid'}</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Assigned Hours</h4>
                      <p className="text-xs font-bold text-slate-800">{placement.agreement?.assignedHoursTotal || placement.assignedHoursTotal || 120} hrs</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Supervisor</h4>
                      <p className="text-xs font-bold text-slate-800">{placement.agreement?.supervisorName || placement.mentorName || 'N/A'}</p>
                  </div>
                  <div>
                      <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 font-mono">Supervisor Contact</h4>
                      <p className="text-xs font-bold text-slate-800">{placement.agreement?.supervisorEmail || placement.registryContact || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Right Section: Details, Progress and Review/Evaluations */}
              <div className="flex-1 p-8 space-y-8">
                {/* Placement Title & Status Line */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                      <div>
                          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{placement.opportunityTitle}</h3>
                          <p className="text-slate-500 text-sm font-medium">{placement.organizationName}</p>
                      </div>
                      <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                          placement.status === 'hired' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}>
                          {placement.status}
                      </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="relative pt-6">
                      <div className="flex items-center justify-between">
                          {['REC', 'APPLIED', 'INTERVIEW', 'DECISION', 'BOARD', 'EVAL 1', 'EVAL 2', 'HIRED'].map((step, idx) => {
                              const isBox = ['REC', 'BOARD', 'HIRED'].includes(step);
                              const isBar = ['APPLIED', 'INTERVIEW', 'DECISION', 'EVAL 1', 'EVAL 2'].includes(step);
                              
                              const hasInterview = 
                                  !!placement.interviewScheduled || 
                                  ['interviewing', 'reviewed', 'accepted', 'placed', 'hired', 'active'].includes(placement.status) ||
                                  (interviews || []).some(i => 
                                      (placement.applicationId && i.applicationId === placement.applicationId) ||
                                      (placement.studentId && i.studentId === placement.studentId) ||
                                      (placement.studentEmail && i.studentEmail === placement.studentEmail) ||
                                      (student?.email && i.studentEmail === student.email)
                                  );

                              const isCompleted = 
                                  (step === 'REC' && (!!placement.isRecommended || recommendations.some(r => r.studentId === placement.studentId && r.opportunityId === placement.opportunityId))) ||
                                  (step === 'APPLIED' && ['applied', 'interviewing', 'reviewed', 'accepted', 'placed', 'hired', 'active'].includes(placement.status)) ||
                                  (step === 'INTERVIEW' && hasInterview) ||
                                  (step === 'DECISION' && ['reviewed', 'accepted', 'placed', 'hired', 'active'].includes(placement.status)) ||
                                  (step === 'BOARD' && ['accepted', 'placed', 'hired', 'active'].includes(placement.status)) ||
                                  (step === 'EVAL 1' && placement.eval1?.status === 'released') ||
                                  (step === 'EVAL 2' && placement.eval2?.status === 'released') ||
                                  (step === 'HIRED' && !!placement.isHiredByOrg);
                                  
                              const isCurrent = step === 'BOARD';
                              
                              let icon = null;
                              if (step === 'REC') icon = <User className={`w-5 h-5 ${isCompleted ? 'text-[#0094c8]' : 'text-slate-400'}`} />;
                              if (step === 'BOARD') icon = <ClipboardCheck className="w-5 h-5 text-[#0094c8]" />;
                              if (step === 'HIRED') icon = <CheckCircle2 className={`w-5 h-5 ${isCompleted ? 'text-[#0094c8]' : 'text-slate-400'}`} />;

                              if (isBox) {
                                  return (
                                      <div key={step} className="flex flex-col items-center relative z-10">
                                          <div className={`flex items-center justify-center transition-all border-2 w-12 h-12 rounded-xl
                                              ${isCompleted 
                                                  ? 'bg-white border-[#0094c8]' 
                                                  : isCurrent 
                                                      ? 'bg-white border-[#0094c8] shadow-[0_0_10px_rgba(0,148,200,0.5)]' 
                                                      : 'bg-white border-slate-200'
                                              }
                                          `}>
                                              {icon}
                                          </div>
                                          <span className={`text-[9px] font-black mt-2 uppercase tracking-tighter ${isCompleted || isCurrent ? 'text-[#0094c8]' : 'text-slate-400'}`}>{step}</span>
                                      </div>
                                  );
                              } else {
                                  return (
                                      <div key={step} className="flex-1 flex flex-col items-center px-1">
                                          <div className={`h-2 w-full rounded-full ${isCompleted ? 'bg-[#0094c8]' : 'bg-slate-200'}`} />
                                          <span className={`text-[9px] font-black mt-2 uppercase tracking-tighter ${isCompleted ? 'text-[#0094c8]' : 'text-slate-400'}`}>{step}</span>
                                      </div>
                                  );
                              }
                          })}
                      </div>
                  </div>

                </div>

                {/* Evaluation Registry Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Placement Evaluation Cycle</h4>
                      <span className="text-[10px] text-slate-400 font-medium italic">Records can be exported as verified PDF logs</span>
                  </div>

                  <div className="space-y-3">
                      {[
                          { num: 1, title: 'Mid-Point Evaluation', id: 'mid', evaluation: placement.eval1, status: placement.eval1?.status || 'pending' },
                          { num: 2, title: 'Final Review', id: 'final', evaluation: placement.eval2, status: placement.eval2?.status || 'pending' }
                      ].map((evalRecord) => (
                          <div key={evalRecord.id} className="bg-white group hover:border-[#0094c8] border border-slate-100 rounded-2xl p-4 flex flex-col gap-3 transition-all">
                              <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-4">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs ${
                                          evalRecord.status === 'released' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
                                      }`}>
                                          {evalRecord.num}
                                      </div>
                                      <div>
                                          <h5 className="text-sm font-bold text-slate-900">{evalRecord.title}</h5>
                                          <div className="flex items-center space-x-2 mt-0.5">
                                              <span className={`text-[10px] font-black uppercase tracking-widest ${
                                                  evalRecord.status === 'released' ? 'text-emerald-500' : 
                                                  evalRecord.status === 'in_progress' ? 'text-amber-500' : 'text-slate-400'
                                              }`}>
                                                  {evalRecord.status === 'released' ? 'Placement Verified' : 
                                                   evalRecord.status === 'in_progress' ? 'Started (Draft)' : 'Awaiting Entry'}
                                              </span>
                                              {evalRecord.evaluation?.dueDate && (
                                                  <>
                                                      <span className="text-slate-300">•</span>
                                                      <span className="text-[10px] text-slate-500 font-medium">
                                                          Due: {new Date(evalRecord.evaluation.dueDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                      </span>
                                                  </>
                                              )}
                                          </div>
                                      </div>
                                  </div>
                                  
                                  {isOrg ? (
                                      <button 
                                          onClick={() => handleStartEval(evalRecord.num as 1 | 2)}
                                          className={`px-4 py-2 border rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                              evalRecord.status === 'released' 
                                                  ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50' 
                                                  : 'bg-[#0094c8] border-[#0094c8] text-white hover:bg-[#007ba6]'
                                          }`}
                                      >
                                          {evalRecord.status === 'released' ? 'View Record' : 'Manage Eval'}
                                      </button>
                                  ) : (
                                      evalRecord.status === 'released' ? (
                                          <button 
                                              onClick={() => handleStartEval(evalRecord.num as 1 | 2)}
                                              className="text-[#0094c8] hover:text-[#007ba6] text-[10px] font-black uppercase tracking-widest flex items-center transition-all active:scale-95"
                                          >
                                              <span>View Scored Record</span>
                                              <ChevronRight className="w-4 h-4 ml-1" />
                                          </button>
                                      ) : (
                                          <div className="text-slate-300 italic text-[10px]">Awaiting Manager</div>
                                      )
                                  )}
                              </div>
                              
                              <div className="flex flex-col gap-1 border-t border-slate-100 pt-3">
                                   <div className="flex items-center text-[10px] text-slate-500">
                                      <CheckCircle2 className={`w-3 h-3 mr-2 ${evalRecord.evaluation?.supervisorSignature ? 'text-emerald-500' : 'text-slate-300'}`} />
                                      Organization Signature
                                   </div>
                                   <div className="flex items-center text-[10px] text-slate-500">
                                      <CheckCircle2 className={`w-3 h-3 mr-2 ${evalRecord.evaluation?.studentSignature ? 'text-emerald-500' : 'text-slate-300'}`} />
                                      Student Signature
                                   </div>
                                   <div className="flex items-center text-[10px] text-slate-500">
                                      <CheckCircle2 className={`w-3 h-3 mr-2 ${evalRecord.evaluation?.advisorSignature ? 'text-emerald-500' : 'text-slate-300'}`} />
                                      Advisor Signature
                                   </div>
                              </div>
                          </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section: Full-Width Placement Notes */}
            <div className="p-6 md:p-8 bg-slate-50/20 w-full">
              <PlacementNotes 
                placementId={placement.id}
                notes={placement.notes}
                userRole={activeUser?.role || ''}
                currentUserUid={activeUser?.uid || ''}
                addNote={addPlacementNote}
                deleteNote={deletePlacementNote}
              />
            </div>
          </div>
        </>
      ) : (
        /* Evaluation Entry Form (95% standard width) */
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden w-full mx-auto">
            <div className="bg-slate-900 p-8 text-white">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-black uppercase tracking-tight">Eval {evalNum}: {evalNum === 1 ? 'Mid-Point' : 'Final Review'}</h2>
                    <div className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest">
                        Draft in Progress
                    </div>
                </div>
                <p className="text-slate-400 text-sm max-w-2xl font-medium">
                    Evaluate {placement.studentName} based on the standard "Profile of the SC Graduate" attributes and specific vocational skills relevant to this worksite.
                </p>
            </div>

            {editingEval.status === 'released' && (
                <div className="mx-8 mt-8 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-tight">This record has been released and verified. Scoring is now locked.</p>
                </div>
            )}

            <div className="p-8 space-y-12">
                {/* SECTION 1: Graduate Attributes */}
                <section className="space-y-8">
                    <div className="flex items-center space-x-3 border-b-2 border-slate-900 pb-2">
                        <span className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-sm">1</span>
                        <h3 className="text-xl font-black uppercase tracking-tight">Employability Attributes</h3>
                    </div>

                    <div className="space-y-10">
                        {GRADUATE_ATTRIBUTES.map((attr) => (
                            <div key={attr.id} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="md:col-span-2">
                                        <h4 className="font-bold text-slate-900 text-base">{attr.label}</h4>
                                        <div className="mt-2 space-y-1">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">Expectations:</p>
                                            <ul className="text-xs text-slate-500 list-disc list-inside space-y-1">
                                                {attr.expectations.map((exp, i) => <li key={i}>{exp}</li>)}
                                            </ul>
                                        </div>
                                    </div>
                                    
                                    <div className="md:col-span-2 space-y-6">
                                        {/* Score Above Comments */}
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Performance Score (1-5)</p>
                                                <div className="group relative">
                                                    <HelpCircle className="w-3 h-3 text-slate-300 cursor-help" />
                                                    <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-slate-900 text-white text-[8px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 leading-tight">
                                                        1: Needs Strategy | 2: Developing | 3: Competent | 4: Proficient | 5: Advanced
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {[1, 2, 3, 4, 5].map((num) => (
                                                    <button
                                                        key={num}
                                                        disabled={editingEval.status === 'released'}
                                                        onClick={() => handleScoreChange(attr.id, num)}
                                                        className={`w-10 h-10 rounded-xl font-black text-xs transition-all border ${
                                                            editingEval.scores[attr.id]?.score === num 
                                                                ? 'bg-slate-900 border-slate-900 text-white shadow-lg scale-110' 
                                                                : 'bg-white border-slate-200 text-slate-400 hover:border-slate-400'
                                                        } ${editingEval.status === 'released' ? 'opacity-80' : ''}`}
                                                    >
                                                        {num}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Evaluator Comments</p>
                                                {editingEval.scores[attr.id]?.score > 0 && editingEval.status !== 'released' && (
                                                    <div className="flex items-center space-x-2">
                                                        {aiError[attr.id] && <span className="text-[9px] font-bold text-red-500 uppercase">{aiError[attr.id]}</span>}
                                                        <button 
                                                            onClick={() => handleAIGenerate(attr.id, attr.label, editingEval.scores[attr.id].score, attr.expectations)}
                                                            disabled={generatingAI[attr.id]}
                                                            className="flex items-center space-x-1.5 px-2.5 py-1 bg-[#0094c8]/10 border border-[#0094c8]/20 rounded-lg text-[9px] font-black text-[#0094c8] uppercase tracking-tight hover:bg-[#0094c8]/20 disabled:opacity-50 transition-all"
                                                        >
                                                            {generatingAI[attr.id] ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                            ) : (
                                                                <Sparkles className="w-3 h-3" />
                                                            )}
                                                            <span>{generatingAI[attr.id] ? 'Drafting...' : 'Oslo AI Assistant'}</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <textarea
                                                value={editingEval.scores[attr.id]?.comments || ''}
                                                onChange={(e) => handleCommentChange(attr.id, e.target.value)}
                                                disabled={editingEval.status === 'released'}
                                                placeholder="Write unique notes on student performance..."
                                                className="w-full h-24 p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs focus:ring-2 focus:ring-slate-900 focus:outline-none resize-none leading-relaxed disabled:opacity-70"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SECTION 2: Workplace & Career Skills */}
                <section className="space-y-8">
                    <div className="flex items-center justify-between border-b-2 border-slate-900 pb-2">
                        <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-sm">2</span>
                            <h3 className="text-xl font-black uppercase tracking-tight">Workplace & Career Skills</h3>
                        </div>
                        {editingEval.status !== 'released' && currentSkillIds.length < 5 && (
                            <button
                                onClick={handleAddSkillSlot}
                                className="flex items-center space-x-1.5 px-3 py-1.5 bg-[#0094c8]/10 border border-[#0094c8]/20 rounded-xl text-[10px] font-black text-[#0094c8] uppercase tracking-widest hover:bg-[#0094c8]/20 transition-all shadow-sm"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Add Skill Focus ({currentSkillIds.length}/5)</span>
                            </button>
                        )}
                    </div>

                    <div className="space-y-10">
                        {currentSkillIds.map((skillId, index) => {
                            const skill = VOCATIONAL_SKILLS.find((s) => s.id === skillId) || VOCATIONAL_SKILLS[0];
                            const isInitialSkill = index < 3;
                            
                            return (
                                <div key={`${skillId}-${index}`} className="space-y-4 border-b border-slate-100 pb-8 last:border-b-0 last:pb-0">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div className="md:col-span-2 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-[10px] font-mono font-bold text-[#0094c8] uppercase tracking-wider">
                                                    {skill.category || 'Career Specific Skill'}
                                                </p>
                                                {!isInitialSkill && editingEval.status !== 'released' && (
                                                    <button
                                                        onClick={() => handleRemoveSkillSlot(index)}
                                                        className="flex items-center space-x-1 text-red-500 hover:text-red-700 transition-colors text-[9px] font-black uppercase tracking-wider"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                        <span>Remove Skill</span>
                                                    </button>
                                                )}
                                            </div>

                                            {editingEval.status !== 'released' ? (
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Choose Skill</label>
                                                    <select
                                                        value={skill.id}
                                                        onChange={(e) => handleSkillSelectionChange(index, e.target.value)}
                                                        className="w-full p-3.5 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-800 uppercase focus:ring-2 focus:ring-slate-900 focus:outline-none shadow-sm cursor-pointer"
                                                    >
                                                        {Object.entries(groupedSkillsByCategory).map(([category, skills]) => (
                                                            <optgroup label={category} key={category} className="text-slate-500 font-bold uppercase text-[10px]">
                                                                {(skills as typeof VOCATIONAL_SKILLS).map(s => (
                                                                    <option 
                                                                        key={s.id} 
                                                                        value={s.id} 
                                                                        disabled={currentSkillIds.includes(s.id) && s.id !== skill.id}
                                                                        className="text-slate-900 font-bold uppercase text-xs"
                                                                    >
                                                                        {s.label}
                                                                    </option>
                                                                ))}
                                                            </optgroup>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Focus Skill</p>
                                                    <h4 className="font-extrabold text-[#011d3c] text-base uppercase tracking-tight">{skill.label}</h4>
                                                </div>
                                            )}
                                            
                                            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Skill Definition</p>
                                                <p className="text-xs text-slate-600 leading-relaxed font-medium">{skill.definition}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="md:col-span-2 space-y-6">
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Performance Score (1-5)</p>
                                                    <div className="group relative">
                                                        <HelpCircle className="w-3 h-3 text-slate-300 cursor-help" />
                                                        <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-slate-900 text-white text-[8px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 leading-tight">
                                                            1: Needs Strategy | 2: Developing | 3: Competent | 4: Proficient | 5: Advanced
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {[1, 2, 3, 4, 5].map((num) => (
                                                        <button
                                                            key={num}
                                                            disabled={editingEval.status === 'released'}
                                                            onClick={() => handleScoreChange(skill.id, num)}
                                                            className={`w-10 h-10 rounded-xl font-black text-xs transition-all border ${
                                                                editingEval.scores[skill.id]?.score === num 
                                                                    ? 'bg-slate-900 border-slate-900 text-white shadow-lg scale-110' 
                                                                    : 'bg-white border-slate-200 text-slate-400 hover:border-slate-400'
                                                            } ${editingEval.status === 'released' ? 'opacity-80' : ''}`}
                                                        >
                                                            {num}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Skill Comments</p>
                                                    {editingEval.scores[skill.id]?.score > 0 && editingEval.status !== 'released' && (
                                                        <div className="flex items-center space-x-2">
                                                            {aiError[skill.id] && <span className="text-[9px] font-bold text-red-500 uppercase">{aiError[skill.id]}</span>}
                                                            <button 
                                                                onClick={() => handleAIGenerate(skill.id, skill.label, editingEval.scores[skill.id].score, [skill.definition])}
                                                                disabled={generatingAI[skill.id]}
                                                                className="flex items-center space-x-1.5 px-2.5 py-1 bg-[#0094c8]/10 border border-[#0094c8]/20 rounded-lg text-[9px] font-black text-[#0094c8] uppercase tracking-tight hover:bg-[#0094c8]/20 disabled:opacity-50 transition-all"
                                                            >
                                                                {generatingAI[skill.id] ? (
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <Sparkles className="w-3 h-3" />
                                                                )}
                                                                <span>{generatingAI[skill.id] ? 'Drafting...' : 'Oslo AI Assistant'}</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <textarea
                                                    value={editingEval.scores[skill.id]?.comments || ''}
                                                    onChange={(e) => handleCommentChange(skill.id, e.target.value)}
                                                    disabled={editingEval.status === 'released'}
                                                    className="w-full h-24 p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs focus:ring-2 focus:ring-slate-900 focus:outline-none resize-none leading-relaxed disabled:opacity-70"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* SECTION 3: Verification & Signatures */}
                <section className="space-y-8 pt-8 border-t border-slate-100">
                    <div className="flex items-center space-x-3 border-b-2 border-slate-900 pb-2">
                        <span className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-sm">3</span>
                        <h3 className="text-xl font-black uppercase tracking-tight">Record Verification</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <SignatureInput 
                            label="Supervisor Signature"
                            value={editingEval.supervisorSignature || ''}
                            onChange={(val) => setEditingEval(prev => prev ? ({...prev, supervisorSignature: val}) : null)}
                            disabled={!canSignAsSupervisor}
                            defaultSignerName={placement.mentorName || (canSignAsSupervisor ? activeUser?.name : undefined)}
                            helperText="Authorized Site Mentor Verification"
                        />

                        <SignatureInput 
                            label="Student Signature"
                            value={editingEval.studentSignature || ''}
                            onChange={(val) => setEditingEval(prev => prev ? ({...prev, studentSignature: val}) : null)}
                            disabled={!canSignAsStudent}
                            defaultSignerName={placement.studentName}
                            helperText="Student Acknowledgment of Results"
                        />

                        <SignatureInput 
                            label="School Advisor Signature"
                            value={editingEval.advisorSignature || ''}
                            onChange={(val) => setEditingEval(prev => prev ? ({...prev, advisorSignature: val}) : null)}
                            disabled={!canSignAsAdvisor}
                            defaultSignerName={activeUser?.name || 'School Advisor'}
                            helperText="Verified by Hub Site Coordinator"
                        />
                    </div>
                </section>

                {/* Form Footer */}
                <div className="pt-8 border-t border-slate-100 flex items-center justify-end space-x-4">
                    <button 
                        onClick={() => { setEvalNum(null); setEditingEval(null); }}
                        className="px-6 py-3 border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all"
                    >
                        {editingEval.status === 'released' ? 'Close Record' : 'Cancel and Return'}
                    </button>
                    {editingEval.status !== 'released' ? (
                      <>
                        <button 
                            onClick={() => handleSaveEval('in_progress')}
                            className="px-6 py-3 bg-white border border-slate-900 text-slate-900 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
                        >
                            Save as Draft
                        </button>
                        <button 
                            onClick={() => handleSaveEval('released')}
                            className="px-8 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
                        >
                            Release and Verify Record
                        </button>
                      </>
                    ) : (
                      <button 
                          onClick={() => handleSaveEval('released')}
                          className="px-8 py-3 bg-[#0094c8] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#007ba6] transition-all shadow-lg"
                      >
                          Save Verification Updates
                      </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {isEditingAgreement && agreementForm && canEditAgreement && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-2xl w-full overflow-hidden animate-fade-in my-8">
            <div className="bg-[#0094c8] p-6 text-white flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black uppercase tracking-tight font-sans">
                  Manage Training Agreement
                </h3>
                <p className="text-blue-100 text-[10px] font-medium uppercase tracking-widest mt-0.5">
                  Answer compliance questions and update official details for {placement.studentName}
                </p>
              </div>
              <button 
                onClick={() => { setIsEditingAgreement(false); setAgreementForm(null); }}
                className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Compliance & WBL Questions Section */}
              <div className="space-y-3 bg-slate-100/60 p-4 rounded-2xl border border-slate-200">
                <h4 className="text-[10px] font-black uppercase text-slate-500 tracking-wider font-mono">
                  WBL Placement Compliance Questions
                </h4>

                <div className="space-y-2.5">
                  {/* Question 1: IGP Alignment */}
                  <div className="p-3 bg-white border border-slate-200/80 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <span className="font-bold text-slate-800 text-xs leading-snug block">
                        Is the Work-Based Learning (WBL) Placement aligned to student's Individual Graduation Plan (IGP)?
                      </span>
                      {agreementForm.isAlignedToIGP === undefined && (
                        <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          Unanswered
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, isAlignedToIGP: true })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.isAlignedToIGP === true
                            ? "bg-[#0094c8] border-[#0094c8] text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        YES
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, isAlignedToIGP: false })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.isAlignedToIGP === false
                            ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        NO
                      </button>
                    </div>
                  </div>

                  {/* Question 2: Paid Placement */}
                  <div className="p-3 bg-white border border-slate-200/80 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <span className="font-bold text-slate-800 text-xs leading-snug block">
                        Is the Work-Based Learning Placement paid?
                      </span>
                      {agreementForm.isPaid === undefined && (
                        <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          Unanswered
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, isPaid: true })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.isPaid === true
                            ? "bg-[#0094c8] border-[#0094c8] text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        YES
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, isPaid: false })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.isPaid === false
                            ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        NO
                      </button>
                    </div>
                  </div>

                  {/* Question 3: Course Credit */}
                  <div className="p-3 bg-white border border-slate-200/80 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <span className="font-bold text-slate-800 text-xs leading-snug block">
                        Did the student earn at least one unit of course credit in the career pathway related to the WBL placement?
                      </span>
                      {agreementForm.earnedCourseCredit === undefined && (
                        <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          Unanswered
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, earnedCourseCredit: true })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.earnedCourseCredit === true
                            ? "bg-[#0094c8] border-[#0094c8] text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        YES
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, earnedCourseCredit: false, courseCode: '' })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.earnedCourseCredit === false
                            ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        NO
                      </button>
                    </div>
                  </div>

                  {/* Question 4: School Insurance */}
                  <div className="p-3 bg-white border border-slate-200/80 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <span className="font-bold text-slate-800 text-xs leading-snug block">
                        Is School Insurance coverage provided?
                      </span>
                      {agreementForm.hasInsurance === undefined && (
                        <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          Unanswered
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, hasInsurance: true })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.hasInsurance === true
                            ? "bg-[#0094c8] border-[#0094c8] text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        YES
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgreementForm({ ...agreementForm, hasInsurance: false })}
                        className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition-all border ${
                          agreementForm.hasInsurance === false
                            ? "bg-slate-900 border-slate-900 text-white shadow-xs"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        NO
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Student's Career Pathway
                  </label>
                  <input
                    type="text"
                    value={agreementForm.careerPathway || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, careerPathway: e.target.value })}
                    placeholder="e.g. Construction"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Course Code (If Credit Earned)
                  </label>
                  <input
                    type="text"
                    disabled={!agreementForm.earnedCourseCredit}
                    value={agreementForm.courseCode || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, courseCode: e.target.value })}
                    placeholder={agreementForm.earnedCourseCredit ? "e.g. CTE-8001" : "Earned course credit must be YES"}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={agreementForm.startDate || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, startDate: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={agreementForm.endDate || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, endDate: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Worksite Name
                  </label>
                  <input
                    type="text"
                    value={agreementForm.worksiteName || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, worksiteName: e.target.value })}
                    placeholder="e.g. Spartanburg School District 8"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Worksite Assigned Hours
                  </label>
                  <input
                    type="number"
                    value={agreementForm.assignedHoursTotal ?? ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, assignedHoursTotal: parseInt(e.target.value) || 0 })}
                    placeholder="e.g. 120"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Worksite Supervisor
                  </label>
                  <input
                    type="text"
                    value={agreementForm.supervisorName || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, supervisorName: e.target.value })}
                    placeholder="e.g. Joshua Loso"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Worksite Phone
                  </label>
                  <input
                    type="text"
                    value={agreementForm.supervisorPhone || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, supervisorPhone: e.target.value })}
                    placeholder="e.g. (864)630-6571"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Worksite Email
                  </label>
                  <input
                    type="email"
                    value={agreementForm.supervisorEmail || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, supervisorEmail: e.target.value })}
                    placeholder="e.g. organization@wblportal.com"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                  Assigned Days of the Week
                </label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                    const active = (agreementForm.assignedDays || []).includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const currentDays = agreementForm.assignedDays || [];
                          const updated = currentDays.includes(day)
                            ? currentDays.filter((d) => d !== day)
                            : [...currentDays, day];
                          setAgreementForm({ ...agreementForm, assignedDays: updated });
                        }}
                        className={`px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-wider transition-all border ${
                          active
                            ? "bg-[#0094c8] border-[#0094c8] text-white"
                            : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Job Description Overview
                  </label>
                  <textarea
                    value={agreementForm.jobDescription || ""}
                    onChange={(e) => setAgreementForm({ ...agreementForm, jobDescription: e.target.value })}
                    rows={4}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-medium focus:outline-none resize-none"
                    placeholder="Enter job description overview..."
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1">
                    Key Learning Objectives (One per line)
                  </label>
                  <textarea
                    value={(agreementForm.learningObjectives || []).join("\n")}
                    onChange={(e) => setAgreementForm({ ...agreementForm, learningObjectives: e.target.value.split("\n") })}
                    rows={4}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs leading-relaxed focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-sans focus:outline-none resize-none"
                    placeholder="Enter learning objectives..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 px-6 flex items-center justify-end space-x-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setIsEditingAgreement(false); setAgreementForm(null); }}
                className="px-4 py-2 border border-slate-200 text-slate-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAgreement}
                className="px-5 py-2 bg-[#0094c8] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#007ba6] transition-colors shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface PlacementNotesProps {
  placementId: string;
  notes?: any[];
  userRole: string;
  currentUserUid: string;
  addNote: (placementId: string, text: string, toStudent: boolean, toAdvisor: boolean) => Promise<void>;
  deleteNote: (placementId: string, noteId: string) => Promise<void>;
}

const PlacementNotes: React.FC<PlacementNotesProps> = ({ 
  placementId, 
  notes = [], 
  userRole, 
  currentUserUid, 
  addNote, 
  deleteNote 
}) => {
  const { showConfirm, showToast } = useInAppDialog();
  const [text, setText] = useState('');
  const [visibleToStudents, setVisibleToStudents] = useState(true);
  const [visibleToAdvisors, setVisibleToAdvisors] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter notes based on role
  const filteredNotes = useMemo(() => {
    return notes.filter(note => {
      // Creator can always see
      if (note.createdBy?.uid === currentUserUid) return true;
      // Admins can always see
      if (userRole === 'superadmin' || userRole === 'regional_admin') return true;
      // Organization/Mentor can see all notes of this placement
      if (userRole === 'organization' || userRole === 'mentor') return true;
      // Student visibility
      if (userRole === 'student' && note.visibleToStudents) return true;
      // Advisor visibility
      if (userRole === 'advisor' && note.visibleToAdvisors) return true;
      return false;
    });
  }, [notes, userRole, currentUserUid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    try {
      setIsSubmitting(true);
      await addNote(placementId, text, visibleToStudents, visibleToAdvisors);
      setText('');
    } catch (err) {
      console.error('Error adding placement note:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOrg = userRole === 'organization';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4 shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div className="flex items-center space-x-1.5">
          <MessageSquare className="w-4 h-4 text-[#0094c8]" />
          <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Placement Notes</h4>
        </div>
        <span className="text-[9px] text-slate-400 font-medium italic">
          {isOrg ? 'Share progress feedback' : 'Official partner coordination ledger'}
        </span>
      </div>

      {/* Note submission form for Organizations */}
      {isOrg && (
        <form onSubmit={handleSubmit} className="space-y-3 bg-slate-50/40 p-3 rounded-xl border border-slate-100">
          <div className="space-y-1">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type notes or feedback here..."
              rows={2}
              maxLength={1000}
              className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0094c8] text-slate-700 font-bold focus:outline-none resize-none placeholder-slate-400"
              required
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-0.5">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setVisibleToStudents(!visibleToStudents)}
                className={`flex items-center space-x-1 px-2 py-1 rounded-md border text-[9px] font-black uppercase tracking-wider transition-all ${
                  visibleToStudents 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                    : 'bg-slate-100 text-slate-400 border-slate-200'
                }`}
              >
                {visibleToStudents ? <Eye className="w-3 h-3 text-emerald-600" /> : <EyeOff className="w-3 h-3 text-slate-400" />}
                <span>To Student</span>
              </button>

              <button
                type="button"
                onClick={() => setVisibleToAdvisors(!visibleToAdvisors)}
                className={`flex items-center space-x-1 px-2 py-1 rounded-md border text-[9px] font-black uppercase tracking-wider transition-all ${
                  visibleToAdvisors 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                    : 'bg-slate-100 text-slate-400 border-slate-200'
                }`}
              >
                {visibleToAdvisors ? <Eye className="w-3 h-3 text-emerald-600" /> : <EyeOff className="w-3 h-3 text-slate-400" />}
                <span>To Advisor</span>
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !text.trim()}
              className="px-3 py-1.5 bg-[#0094c8] text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-[#007ba6] disabled:opacity-50 transition-all flex items-center space-x-1"
            >
              <Plus className="w-3 h-3" />
              <span>Add Note</span>
            </button>
          </div>
        </form>
      )}

      {/* Notes list */}
      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent scroll-smooth">
        {filteredNotes.length === 0 ? (
          <div className="text-center py-4 text-slate-400 text-[11px] italic">
            No notes have been recorded for this placement yet.
          </div>
        ) : (
          filteredNotes
            .slice()
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((note) => {
              const isOwner = note.createdBy?.uid === currentUserUid;
              const dateStr = new Date(note.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });

              return (
                <div key={note.id} className="border border-slate-100 rounded-xl p-2.5 space-y-1.5 hover:border-[#0094c8]/30 transition-all bg-slate-50/20">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-black text-[11px] text-slate-900 uppercase">
                        {note.createdBy?.name || 'Partner Manager'}
                      </span>
                      <span className="text-[7px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-black uppercase tracking-wider font-mono">
                        {note.createdBy?.role || 'Org'}
                      </span>
                      <span className="text-[9px] text-slate-400 font-semibold">{dateStr}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Compact visibility indicators */}
                      <div className="flex items-center space-x-1 text-[8px] font-black uppercase text-slate-400 bg-white border border-slate-100 px-1.5 py-0.5 rounded-md">
                        <span className={`flex items-center space-x-0.5 ${note.visibleToStudents ? 'text-emerald-600' : 'text-slate-400'}`}>
                          <span>STU</span>
                          {note.visibleToStudents ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5 text-slate-300" />}
                        </span>
                        <span className="text-slate-200">|</span>
                        <span className={`flex items-center space-x-0.5 ${note.visibleToAdvisors ? 'text-emerald-600' : 'text-slate-400'}`}>
                          <span>ADV</span>
                          {note.visibleToAdvisors ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5 text-slate-300" />}
                        </span>
                      </div>

                      {/* Delete button (only owner, org, or admins can delete) */}
                      {(isOwner || isOrg || userRole === 'superadmin' || userRole === 'regional_admin') && (
                        <button
                          onClick={async () => {
                            const confirmed = await showConfirm({
                              title: 'Delete Note',
                              message: 'Are you sure you want to delete this placement note?',
                              confirmText: 'Delete Note',
                              type: 'danger',
                            });
                            if (confirmed) {
                              deleteNote(placementId, note.id);
                              showToast({
                                title: 'Note Deleted',
                                message: 'Placement note was deleted.',
                                type: 'info',
                              });
                            }
                          }}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-slate-50 rounded transition-colors cursor-pointer"
                          title="Delete note"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed font-bold whitespace-pre-line">
                    {note.text}
                  </p>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
};
